import StarRounded from '@mui/icons-material/StarRounded';
import Box from '@mui/material/Box';
import React from 'react';

/**
 * The container every rate card is rendered in, wherever it is surfaced — the order/project
 * card popover and the job row's provenance tooltip.
 *
 * Its one job is to keep the **starred** treatment identical across those surfaces. The card
 * starred on an order (`order.default_site_rate_schedule`) and the card a job's rate was
 * stamped from are the *same object*, so showing them two different ways read as two
 * different concepts. Starred cards get the brand-yellow border and badge; everything else
 * gets the plain surface border.
 */

interface RateCardShellProps {
  /**
   * Marks this as the starred card on the order. Drives the yellow border and sunken fill so
   * it reads first among sibling cards.
   */
  isStarred?: boolean;
  /** Badge / label row rendered above the card body. */
  badge?: React.ReactNode;
  children: React.ReactNode;
}

export default function RateCardShell({
  isStarred = false,
  badge,
  children,
}: RateCardShellProps) {
  return (
    // Each card gets a real container so the eye can find its edges, rather than relying on a
    // hairline rule between otherwise-identical text blocks.
    <Box
      sx={(theme) => ({
        backgroundColor: isStarred
          ? theme.semantic.surfaceSunken
          : theme.semantic.surface,
        border: `1px solid ${
          isStarred ? theme.brandV2.colors.treadYellow : theme.semantic.border
        }`,
        borderRadius: '0.375rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: 1.25,
      })}
    >
      {badge}
      {/* minWidth 0 lets a long card name wrap instead of widening the container. */}
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

interface StarredRateCardBadgeProps {
  label: string;
}

export function StarredRateCardBadge({ label }: StarredRateCardBadgeProps) {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        alignSelf: 'flex-start',
        backgroundColor: theme.brandV2.colors.treadYellow,
        borderRadius: '0.25rem',
        // MUST be textOnBrand, not treadBlack: `brandV2.colors.treadBlack` follows the
        // dark-mode ink ramp and flips to near-white, which rendered this badge at 1.08:1 on
        // the yellow fill. textOnBrand is black in both modes.
        color: theme.semantic.textOnBrand,
        display: 'inline-flex',
        fontSize: '0.625rem',
        fontWeight: 700,
        gap: 0.25,
        letterSpacing: '0.04em',
        px: 0.5,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      })}
    >
      {/* Decorative: the badge's own text is the accessible name, so labelling the icon as
          well would have a screen reader announce it twice. */}
      <StarRounded aria-hidden sx={{ fontSize: '0.75rem' }} />
      {label}
    </Box>
  );
}
